import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type CatalogKey =
  | 'datacenter'
  | 'isp'
  | 'mobile'
  | 'residential'
  | 'consumer'
  | 'temporary'
  | 'permanent';

const ITEMS: {
  key: CatalogKey;
  to: string;
  accent: string;
  icon: React.ReactElement;
}[] = [
  {
    key: 'datacenter',
    to: '/datacenter-proxies',
    accent: 'blue',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="dcA" x1="12" y1="8" x2="52" y2="56" gradientUnits="userSpaceOnUse">
            <stop stopColor="#93c5fd" />
            <stop offset="1" stopColor="#2563eb" />
          </linearGradient>
        </defs>
        <rect x="14" y="10" width="36" height="12" rx="3" fill="url(#dcA)" />
        <rect x="14" y="26" width="36" height="12" rx="3" fill="url(#dcA)" opacity="0.92" />
        <rect x="14" y="42" width="36" height="12" rx="3" fill="url(#dcA)" opacity="0.82" />
        <circle cx="20" cy="16" r="2" fill="#fff" />
        <circle cx="20" cy="32" r="2" fill="#fff" />
        <circle cx="20" cy="48" r="2" fill="#fff" />
      </svg>
    ),
  },
  {
    key: 'isp',
    to: '/isp-proxies',
    accent: 'cyan',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="ispA" x1="16" y1="18" x2="48" y2="52" gradientUnits="userSpaceOnUse">
            <stop stopColor="#7dd3fc" />
            <stop offset="1" stopColor="#0284c7" />
          </linearGradient>
        </defs>
        <path d="M18 22c4.5-5.5 23.5-5.5 28 0" stroke="#7dd3fc" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.55" />
        <path d="M22 27c3-3.8 17-3.8 20 0" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.8" />
        <rect x="22" y="32" width="20" height="16" rx="3" fill="url(#ispA)" />
        <circle cx="32" cy="28" r="3.5" fill="#0ea5e9" stroke="#e0f2fe" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    key: 'mobile',
    to: '/mobile-proxies',
    accent: 'indigo',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="mobA" x1="18" y1="8" x2="44" y2="54" gradientUnits="userSpaceOnUse">
            <stop stopColor="#a5b4fc" />
            <stop offset="1" stopColor="#4338ca" />
          </linearGradient>
        </defs>
        <rect x="20" y="8" width="20" height="36" rx="4" fill="url(#mobA)" />
        <rect x="23" y="13" width="14" height="22" rx="1.5" fill="#e0e7ff" />
        <text x="30" y="27" textAnchor="middle" fill="#312e81" fontSize="8" fontWeight="800">5G</text>
      </svg>
    ),
  },
  {
    key: 'residential',
    to: '/residential-proxies',
    accent: 'sky',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="resA" x1="10" y1="14" x2="50" y2="52" gradientUnits="userSpaceOnUse">
            <stop stopColor="#7dd3fc" />
            <stop offset="1" stopColor="#0369a1" />
          </linearGradient>
        </defs>
        <path d="M10 30 L32 12 L54 30 V52 H10 Z" fill="url(#resA)" />
        <rect x="26" y="34" width="12" height="18" rx="1.5" fill="#e0f2fe" />
      </svg>
    ),
  },
  {
    key: 'consumer',
    to: '/residential-proxies',
    accent: 'blue',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="conA" x1="12" y1="12" x2="52" y2="52" gradientUnits="userSpaceOnUse">
            <stop stopColor="#93c5fd" />
            <stop offset="1" stopColor="#1d4ed8" />
          </linearGradient>
        </defs>
        <circle cx="28" cy="28" r="16" stroke="url(#conA)" strokeWidth="3" fill="rgba(59,130,246,0.12)" />
        <rect x="40" y="30" width="12" height="20" rx="2.5" fill="url(#conA)" />
        <text x="46" y="42" textAnchor="middle" fill="#1e3a8a" fontSize="5.5" fontWeight="800">GB</text>
      </svg>
    ),
  },
  {
    key: 'temporary',
    to: '/temporary-numbers',
    accent: 'violet',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="tmpA" x1="16" y1="8" x2="48" y2="52" gradientUnits="userSpaceOnUse">
            <stop stopColor="#c4b5fd" />
            <stop offset="1" stopColor="#6d28d9" />
          </linearGradient>
        </defs>
        <rect x="16" y="8" width="20" height="36" rx="4" fill="url(#tmpA)" />
        <circle cx="42" cy="18" r="9" fill="#a78bfa" />
        <circle cx="42" cy="18" r="6.5" fill="#ede9fe" />
      </svg>
    ),
  },
  {
    key: 'permanent',
    to: '/permanent-numbers/purchase',
    accent: 'emerald',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="perA" x1="16" y1="8" x2="44" y2="48" gradientUnits="userSpaceOnUse">
            <stop stopColor="#6ee7b7" />
            <stop offset="1" stopColor="#047857" />
          </linearGradient>
        </defs>
        <rect x="16" y="8" width="20" height="36" rx="4" fill="url(#perA)" />
        <path d="M36 34c3.2-1.4 6.5 1.2 6.5 4.2S42 42.5 38.8 41.5c-3.2 1.4-6.5-1.2-6.5-4.2S32.8 32.6 36 34z" fill="#34d399" />
        <path d="M46 34c3.2-1.4 6.5 1.2 6.5 4.2S52 42.5 48.8 41.5c-3.2 1.4-6.5-1.2-6.5-4.2S42.8 32.6 46 34z" fill="#6ee7b7" />
      </svg>
    ),
  },
];

export function HomeCatalog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<CatalogKey | null>(null);

  return (
    <section className="dx-catalog" id="catalog">
      <div className="dx-catalog__list">
        {ITEMS.map((item) => {
          const isOpen = open === item.key;
          const details = t(`home.catalog.${item.key}.details`, { returnObjects: true });
          const lines = Array.isArray(details) ? (details as string[]) : [];
          return (
            <article key={item.key} className={`dx-cat-item ${isOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className={`dx-cat-card dx-cat-card--${item.accent}`}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : item.key)}
              >
                <div className="dx-cat-card__icon">{item.icon}</div>
                <div className="dx-cat-card__body">
                  <h3>
                    {t(`home.catalog.${item.key}.titleBefore`)}{' '}
                    <em>{t(`home.catalog.${item.key}.titleAccent`)}</em>
                  </h3>
                  <p>{t(`home.catalog.${item.key}.description`)}</p>
                  <div className="dx-cat-card__tags">
                    <span>{t(`home.catalog.${item.key}.tag1`)}</span>
                    <span>{t(`home.catalog.${item.key}.tag2`)}</span>
                  </div>
                </div>
                <div className="dx-cat-card__go" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </button>
              {isOpen && (
                <div className="dx-cat-detail">
                  <ul>
                    {lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <Link to={item.to} className="dx-cat-detail__cta">
                    {t('home.catalog.learnMore', 'Learn more')}
                  </Link>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
